"use client"

import { createContext, useCallback, useContext } from "react"
import type { RefObject } from "react"
import type { SpotlightRequest } from "@/components/GlobalSearchDialog"
import type { WaitingAnswer } from "./useWaitingAnswer"

type TopBarSlotName =
    "back" | "left" | "leftMeta" | "center" | "right" | "controls"

type SlotClaims = Record<TopBarSlotName, number>

export interface TopBarContextValue {
    slots: Record<TopBarSlotName, HTMLElement | null>
    registerSlot: (name: TopBarSlotName, el: HTMLElement | null) => void
    claimSlot: (name: TopBarSlotName) => () => void
    claims: SlotClaims
    openSearch: () => void
    openSpotlight: (request: SpotlightRequest) => void
    isSpotlightOpen: boolean
    isSpotlightSearching: boolean
    isSearchExpanded: boolean
    expandSearch: () => void
    collapseSearch: () => void
    dismissSearch: () => void
    setShouldKeepCenterOpen: (shouldKeepOpen: boolean) => void
    shouldKeepCenterOpen: boolean
    setCenterSearchHint: (hint: string) => void
    centerSearchHint: string
    setWilsonSubject: (subject: string) => void
    wilsonSubject: string
    /** Run when the center slot's words are sent to Wilson as a question. */
    centerOnAsked: RefObject<(() => void) | undefined>
    setIslandWidth: (width: number) => void
    setAskPanelBottom: (bottom: number) => void
    isAsking: boolean
    askingQuestion: string | undefined
    isQuickAskAvailable: boolean
    startAsking: (question?: string) => void
    stopAsking: () => void
    /** A conversation left with Wilson still working, until it is read. */
    waitingAnswer: WaitingAnswer | null
    isResuming: boolean
    openWaitingAnswer: () => void
}

export const TopBarContext = createContext<TopBarContextValue | null>(null)

export function useOpenChatThread(): (threadId: string) => void {
    const ctx = useContext(TopBarContext)
    const openSpotlight = ctx?.openSpotlight
    return useCallback(
        (threadId: string) => openSpotlight?.({ isChat: true, threadId }),
        [openSpotlight]
    )
}
